// src/services/avatarProfile.js — the avatar becomes the profile picture (web).
//
// Hand-written for the browser (the phone's services/avatarProfile.ts is the
// same shape over React Native storage); the FIELDS it writes come from the
// generated twin services/avatar3d.js, so both clients write one document.
// See that file for why the avatar IS the profile picture while it exists.
// Nothing but the spec and the rendered picture is stored: the figure is
// assembled by every viewer from our own catalogue (services/avatarKit.js).

import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { auth, db, storage } from '../config/firebase';
import { avatarSaveFields, avatarRemoveFields, hasAvatar } from './avatar3d';
import { normalizeSpec } from './avatarKit';
import { warmAvatar } from './avatarKitLoader';

export function toFirestoreUpdate(fields) {
  const out = {};
  for (const k of Object.keys(fields)) out[k] = fields[k] === null ? deleteField() : stripUndefined(fields[k]);
  return out;
}

/** Firestore rejects `undefined` anywhere in a document; an optional field is simply absent. */
export function stripUndefined(v) {
  if (Array.isArray(v)) return v.map(stripUndefined);
  if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
    const o = {};
    for (const k of Object.keys(v)) { const x = v[k]; if (x !== undefined) o[k] = stripUndefined(x); }
    return o;
  }
  return v;
}

/** `snapshot` is a PNG Blob from Avatar3D.snapshot(). */
export async function saveAvatarToProfile(uid, profile, spec, snapshot, now = Date.now()) {
  const s = normalizeSpec(spec);
  const fileRef = storageRef(storage, `profileImages/${uid}/avatar-${Math.floor(now)}.png`);
  await uploadBytes(fileRef, snapshot, { contentType: 'image/png' });
  const snapshotUrl = await getDownloadURL(fileRef);
  const fields = avatarSaveFields(profile, s, snapshotUrl, now);
  await updateDoc(doc(db, 'users', uid), toFirestoreUpdate(fields));
  if (auth.currentUser && auth.currentUser.uid === uid) {
    await updateProfile(auth.currentUser, { photoURL: snapshotUrl }).catch(() => {});
  }
  warmAvatar(fields.avatar);
  return fields;
}

export async function removeAvatarFromProfile(uid, profile, now = Date.now()) {
  if (!hasAvatar(profile)) return {};
  const fields = avatarRemoveFields(profile, now);
  await updateDoc(doc(db, 'users', uid), toFirestoreUpdate(fields));
  if (auth.currentUser && auth.currentUser.uid === uid) {
    await updateProfile(auth.currentUser, { photoURL: fields.profileImage || null }).catch(() => {});
  }
  return fields;
}
